// App bootstrap (loaded by main.tsx): read the stored options, load the language, create the
// engine and the UI store, wire option side effects and engine events, preload the deck images,
// deal, render. Start-up windows like legacy: the Games window on desktop (not on /my-score, not
// for the multiplayer / embedded-rtp targets), the win preview on /my-score pages.
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { env } from './env';
import { createGame } from './engine/controller';
import { BIG_CARD_MAX_W, CARD_MAX_W, connectLayoutFor, mahjongLayoutFor, mahjongLayoutsFor } from './board/geometry';
import { MAHJONG_LAYOUTS } from './engine/rules/mahjong';
import { tallFromWide } from './engine/rules/mahjongConnect';
import { pickedMahjongLayout, pickMahjongLayout } from './options/mahjongLayout';
import { watchNoMoves } from './ui/mahjongHelp';
import { watchLostGame } from './ui/lostGame';
import { installTapClick } from './ui/tapClick';
import { layoutOf, RULES } from './engine/rules';
import type { GameId } from './engine/types';
import { GameContext } from './store/gameStore';
import { createUiStore, setUiStore, uiStore } from './store/uiStore';
import { backImage, backgroundSrc, bigCardsProto, emptySlotSrc, faceImages, preloadImages, tileDeckOf, tileImages, type PreloadImage } from './options/assets';
import { MAHJONG_BONUS, MAHJONG_TILES } from './engine/cards';
import { playSound, rememberLastSound, setSoundBank, startSoundBuilding } from './sound/sound';
import { installTestHook } from './test-hook';
import { Assist, localAssistStorage, workerSearch } from './assist/assist';
import { App, armMenuAutoHide, type ShellActions } from './app/App';
import type { WinData } from './windows/WinWindow';
import { silentLogin } from './account/player';
import { Mp2Client } from './multiplayer/client2';
import { createMultiplayerStore, fetchTicket, setMultiplayerStore } from './multiplayer/store';
import { createOnlineCards, type OnlineCardGame } from './online';
import { setSeatWaits } from './online/common';
import { normalizeGameId as toGameId } from './engine/rules';
import { injectIcons } from './ui/icons';
import { installVvh } from './ui/vvh';
import { installTapToast } from './ui/tapToast';
import { installBackGuard } from './ui/backGuard';
import { installPillRedraw } from './ui/pillRedraw';
import { applyEffects, effectsOff, effectsOnFor, measureFrames } from './ui/effects';
import { startupDone, withIndicator } from './ui/busy';
import { reportError, setReportContext } from './ui/errorReport';
import { reportPlay } from './ui/playReport';
import { currentLanguage, t } from './i18n';
import type { EngineState } from './engine/controller';
import './app/app.css';
import { CARD_IDS, game, isMobile, options, pageData, setBackground, sharedPattern, smallBackground } from './app-prelude';
import { animationDone } from './ui/compat';
import { onDragBack } from './input/dragBack';
import { installDomGuard, pageTranslated } from './ui/domGuard';
import { createHeartsStore, heartsBoardText, setHeartsStore } from './hearts/store';
import { leaders as heartsLeaders, type HeartsState } from './hearts/rules';
import { createMakaoStore, makaoBoardText, setMakaoStore } from './makao/store';
import { winScore as makaoWinScore, type MakaoState } from './makao/rules';
import { createKulkiStore, kulkiBoardText, setKulkiStore } from './kulki/store';
import { pickKulkiLine, pickedKulkiLine } from './options/lineKulki';
import { COLOURS, type KulkiState } from './kulki/rules';
import { ballSetOf, ballSrc } from './kulki/balls';
import { boardText358, create358Store, set358Store } from './358/store';
import { playerWon as won358, winScore as winScore358, type State358 } from './358/rules';
import { pick358Length, picked358Length } from './options/length358';
import { boardText1000, create1000Store, set1000Store } from './1000/store';
import { playerWon as won1000, winScore as winScore1000, type State1000 } from './1000/rules';
import { pick1000Target, picked1000Target } from './options/target1000';
import { pickedSpiderColour, spiderSuits } from './options/spiderColour';
import { post } from './api/client';
import { winHash } from './api/hashes';

(window.__bootStages ??= {})['entry-start'] = Math.round(performance.now()); // the start watchdog's steps (main.tsx): the module's own work begins


const ctrl = createGame({
  game,
  scoring: options.option.scoring,
  autoFlip: options.option.autoFlip,
  autoMove: options.option.autoMove,
});
const ui = createUiStore(options);
setUiStore(ui);
if (isMobile) document.body.id = 'mobile';
setSoundBank(options.option.sound);
// 3D effects (ui/effects.ts): at once from what the browser tells and the last measurement; untouched by the
// player, the frames are measured once a few seconds after the start (the deal is over by then)
applyEffects(effectsOnFor(options.option.effects));
window.setTimeout(() => {
  measureFrames(() => {
    if (ui.getState().options.option.effects === 'auto') applyEffects(effectsOnFor('auto'));
  });
}, 5000);

ui.onOptionChange = (key, _value, opts) => {
  if (key === 'effects') applyEffects(effectsOnFor(opts.option.effects));
  if (key === 'scoring') ctrl.setOptions({ scoring: opts.option.scoring });
  if (key === 'autoFlip') ctrl.setOptions({ autoFlip: opts.option.autoFlip });
  if (key === 'autoMove') ctrl.setOptions({ autoMove: opts.option.autoMove });
  if (key === 'sound') {
    setSoundBank(opts.option.sound);
    const bank = opts.option.sound;
    if (bank === 'paper' || bank === 'plastic') rememberLastSound(bank); // the footer toggle restores it (Q-FOOT-1); not in the opt cookie
  }
};
// A theme / deck / back change: preload the new images with the indicator shown (legacy options.js did
// the same), so the cards switch in one go instead of popping in one by one.
let lastSkin = { ...options.skin };
ui.onSkinChange = (skin) => {
  setBackground(skin.backgroundType);
  const urls: PreloadImage[] = [];
  if (skin.deckType !== lastSkin.deckType) urls.push(...faceImages(skin.deckType, CARD_IDS));
  if (skin.cardBackType !== lastSkin.cardBackType) urls.push(backImage(skin.cardBackType));
  if (skin.backgroundType !== lastSkin.backgroundType)
    urls.push(backgroundSrc(skin.backgroundType, smallBackground()), emptySlotSrc(skin.backgroundType));
  lastSkin = { ...skin };
  if (urls.length) void withIndicator(() => preloadImages(urls));
};
ui.onWindowClose = (name) => {
  // Legacy menu.js: the 3 s auto-hide is armed on every close of these windows and checks the menu
  // when it fires (a menu opened in the meantime is closed too).
  if (!['options', 'games', 'opt_skin', 'info'].includes(name)) return;
  // Under 840 px the menu is a sheet over the board (app.css): it closes with the window it opened, else
  // it would catch the next taps on the cards for 3 s (UI restyle 2026-10-16).
  if (matchMedia('(max-width: 839px)').matches) {
    if (ui.getState().menuOpen) ui.set({ menuOpen: false });
  } else armMenuAutoHide();
};

// ---- multiplayer -----------------------------------------------------------------------------
// protocol v2 (.ai/multiplayer-protocol-v2.md): guests are named by the server from this word ("Gość", "Guest")
const mpClient = new Mp2Client(fetchTicket, () => t('multiplayer.guest-name', { random: '' }).replace(/[-\s]+$/, ''));
const mp = createMultiplayerStore(mpClient);
setMultiplayerStore(mp);
let mpMode = false;
/** The online card game on the table (online/): its store stands in for the offline one; null = none. */
let mpCards: string | null = null;
/** The pattern of the last Kulki race: that game, played on after the race, never goes to the solo stats. */
let raceKulki: string | null = null;
let online: Record<string, OnlineCardGame> | null = null;
const onlineCards = () =>
  (online ??= createOnlineCards({
    send: (m) => mp.move(m),
    openWindow: (name) => ui.openWindow(name),
    closeWindow: (name) => {
      if (ui.isOpen(name)) ui.closeWindow(name);
    },
    onStep: step,
  }));
mp.setHooks({
  startGame(name, pattern) {
    mpMode = true;
    ui.set({ menuOpen: false }); // the menu slides away at the start (MP-015)
    ctrl.setOptions({ scoring: 'standard' });
    lastStats = { score: 0, moves: 0 };
    mp.updateGameStats(lastStats); // legacy reported 0/0 at the start and again after the deal
    if (ctrl.getState().status === 'dealing') return;
    const id = toGameId(name);
    // Mahjong Connect: the server deals the wide grid; a board taller than wide gets the same tiles turned onto the tall
    // grid (as hard, the same pairs). Mahjong keeps the deal's own shape (the Tower, which fits every board).
    let deal = pattern;
    if (id === 'mahjong-connect' && pattern.length === 145 && pattern[144] === 0 && connectLayoutFor(...mahjongBoard()) === 1)
      deal = tallFromWide(pattern);
    raceKulki = id === 'kulki' ? deal.join(',') : null;
    if (id === ctrl.getState().game) ctrl.newGame({ pattern: deal });
    else ctrl.switchGame(id, { pattern: deal });
  },
  finishGame() {
    mpMode = false;
    ctrl.stopTimer();
    if (RULES[ctrl.getState().game].table === 'kulki') kulki.stop(); // the race is over: the board's clock stops
    ctrl.setOptions({ scoring: ui.getState().options.option.scoring }); // restored after the round (GAP-011 fixed)
  },
  cancelGame() {
    mpMode = false;
    ctrl.stopTimer(); // the opponent left or I did: the clock stops (MP-013)
    if (RULES[ctrl.getState().game].table === 'kulki') kulki.stop();
    ctrl.setOptions({ scoring: ui.getState().options.option.scoring });
  },
  doneGame() {
    ctrl.stopTimer(); // "I'm done": my time stops; the waiting window covers the board until the result
    if (RULES[ctrl.getState().game].table === 'kulki') kulki.stop();
  },
  // a card game online (Makao, Kierki; owner 2026-10-29): the server deals and plays the bots; the app shows the game
  // with its online store and never deals it here
  startCards(start) {
    const g = onlineCards()[start.game];
    if (!g) return;
    mpCards = start.game;
    setSeatWaits(undefined);
    ui.set({ menuOpen: false });
    g.begin(start);
    const id = toGameId(start.game);
    if (id !== ctrl.getState().game) ctrl.switchGame(id);
  },
  cardState(f) {
    if (!mpCards) return;
    onlineCards()[mpCards]?.state(f);
    setSeatWaits(f.turns); // who we wait for: a clock by their name after a while (online/TurnClock.tsx SeatWait)
  },
  endCards(f) {
    setSeatWaits(undefined);
    if (mpCards) onlineCards()[mpCards]?.end(f);
  },
  cardSeat(seat, name, bot, flag) {
    if (mpCards) onlineCards()[mpCards]?.rename(seat, name, bot, flag);
  },
  stopCards() {
    const g = mpCards;
    mpCards = null;
    setSeatWaits(undefined);
    if (!g) return;
    onlineCards()[g]?.stop();
    ctrl.newGame(); // the offline game again, a new deal
  },
});
let lastStats = { score: 0, moves: 0 };
ctrl.subscribe((s) => {
  if (!mpMode || RULES[s.game].table === 'kulki') return; // Kulki's score is its own store's (below, after the store)
  if (s.score !== lastStats.score || s.moves !== lastStats.moves) {
    lastStats = { score: s.score, moves: s.moves };
    mp.updateGameStats(lastStats);
  }
});

/** Resolves once every card has landed: the engine commits a move at once and the cards glide to it (CSS
 *  transitions). The win window waits for the last card (owner 2026-10-17: it came while the last two were
 *  still flying up). Two frames let React render the commit and the transitions start; at most 1 s. */
function afterCardsLand(): Promise<void> {
  const frame = () => new Promise<void>((r) => requestAnimationFrame(() => r()));
  // Chrome < 84 has no getAnimations() (Samsung Internet 13 = Chrome 83, Chrome 81 on Android 4.4): the wait threw and
  // the Win window never opened. There it waits the whole second.
  if (typeof document.getAnimations !== 'function') return new Promise<void>((r) => setTimeout(r, 1000));
  const landed = frame()
    .then(frame)
    .then(() =>
      Promise.all(
        document
          .getAnimations()
          .filter((a) => (a as CSSTransition).transitionProperty === 'transform' && ((a.effect as KeyframeEffect | null)?.target as Element | null)?.classList.contains('card'))
          .map(animationDone),
      ),
    )
    .then(() => undefined, () => undefined); // a failure here must never keep the Win window away
  return Promise.race([landed, new Promise<void>((r) => setTimeout(r, 1000))]);
}

ctrl.onEvent((e) => {
  if (e.type === 'sound' && e.name === 'win' && !mpMode) return; // played with the win window, below
  // A deal behind an open window (the Games window on start, any other) is silent (owner 2026-10-17: no
  // strange, surprising sounds)
  if (e.type === 'sound' && e.name === 'deal' && ui.getState().windows.some((w) => !w.closing && !w.minimized)) return;
  if (e.type === 'sound') playSound(e.name, e); // e carries the flight (and a deal's cards and timing)
  if (e.type === 'dealt' && mpMode) {
    lastStats = { score: 0, moves: 0 };
    mp.updateGameStats(lastStats);
    ctrl.startTimer(); // multiplayer: the clock runs from the deal (TIME-009)
  }
  if (e.type === 'won') {
    if (mpMode) {
      mp.finishGame();
      return;
    }
    const s = ctrl.getState();
    const data: WinData = {
      game: s.game,
      scoring: s.scoring,
      time: s.seconds,
      score: s.score,
      moves: s.moves,
      prevDeal: s.isRetry,
      pattern: s.pattern.slice(),
    };
    const deal = s.deals;
    void afterCardsLand().then(() => {
      const now = ctrl.getState();
      if (now.deals !== deal || now.status !== 'won') return; // a new game came first: that win is not shown
      playSound('win');
      ui.openWindow('win', data as unknown as Record<string, unknown>);
    });
  }
});

// A game chosen while a deal runs: saved at once, dealt when that deal is over (owner 2026-10-24, player reports
// "40 thieves ... switched to a different game", "chcę normalny pasjans": the Games window opens during the
// start-up deal, and a Play in its first seconds used to be dropped without a word, the old game kept and saved).
let switchAfterDeal: GameId | null = null;
ctrl.subscribe((s) => {
  if (!switchAfterDeal || s.status === 'dealing') return;
  const next = switchAfterDeal;
  switchAfterDeal = null;
  // after this commit's listeners (a switch from inside one would deal before the others saw the deal end)
  if (next !== s.game) queueMicrotask(() => ctrl.switchGame(next));
});

const actions: ShellActions = {
  play(next, layout, shapeBefore, colourBefore) {
    const st = ctrl.getState();
    ui.closeWindow('games');
    if (st.status === 'won' && !ui.isOpen('win')) return; // the last card still flies up: the win is not lost (GAP-007)
    // Mahjong: a shape picked in the Games window (null: Mahjong itself, a random shape a deal); a shape other than
    // the running deal's deals a new game in it
    // (owner 2026-10-27: "kliknięcie w mahjonga (główny wpis) załaduje random": back from a picked shape to Random deals
    // a new game too)
    const before = shapeBefore !== undefined ? shapeBefore : pickedMahjongLayout();
    if (layout !== undefined && next === 'mahjong') pickMahjongLayout(layout);
    const newShape = layout !== undefined && next === 'mahjong' && next === st.game && (layout !== null ? st.layout !== layout : before !== null);
    // 3-5-8: the length picked in the Games window; another length than the running game's deals a new game in it
    if (typeof layout === 'number' && RULES[next].table === '358') pick358Length(layout);
    const newLength = typeof layout === 'number' && RULES[next].table === '358' && next === st.game && t58.getState().game?.length !== layout;
    // Tysiąc: the same for the points the game is played to
    if (typeof layout === 'number' && RULES[next].table === '1000') pick1000Target(layout);
    const newTarget = typeof layout === 'number' && RULES[next].table === '1000' && next === st.game && t1k.getState().game?.target !== layout;
    // Kulki: the same for its version (5 or 4 in a row)
    if (typeof layout === 'number' && RULES[next].table === 'kulki') pickKulkiLine(layout);
    const newLine = typeof layout === 'number' && RULES[next].table === 'kulki' && next === st.game && kulki.getState().game?.line !== layout;
    // Spider one suit: the colour picked in the Games window (saved there at once); another colour than the running
    // deal's, or back to Random from a colour, deals a new game
    const colour = pickedSpiderColour();
    const newColour = next === 'spider:one-suit' && next === st.game && colourBefore !== undefined && (colour !== null ? st.suits[0] !== colour : colourBefore !== null);
    // Play with the running game saves nothing: a game dealt from a link (/#<game>, an SEO game page) is not
    // saved (owner 2026-10-24: one Play there kept the player in that game on every later visit)
    if (next === st.game && !switchAfterDeal) {
      if (ui.isOpen('win')) ui.closeWindow('win');
      if (st.status === 'dealing') ctrl.finishDeal(); // Play with the game being dealt: its deal jumps to the end
      if (newShape || newLength || newTarget || newColour || newLine) ctrl.newGame();
      return;
    }
    ui.setOption('game', next);
    // a running deal no longer holds Play up (owner 2026-10-24: "animacja nie powinna blokować wciskania"; it used to
    // wait for the deal's end, FIX-008): the deal jumps to its end, then the other game is dealt
    if (st.status === 'dealing') ctrl.finishDeal();
    switchAfterDeal = null;
    if (ui.isOpen('win')) ui.closeWindow('win'); // switching closes the win window (GAP-008)
    if (next !== st.game) ctrl.switchGame(next);
  },
  togglePause() {
    // Same guard as the P key: no pause while the Info or Auth window is open (legacy menu.js).
    if (ui.isOpen('info') || ui.isOpen('auth')) return;
    if (ui.isOpen('pause')) actions.resume();
    else if (ctrl.getState().status === 'idle' || ctrl.getState().status === 'busy') {
      ctrl.pause();
      ui.openWindow('pause');
    }
  },
  resume() {
    ui.closeWindow('pause');
    ctrl.resume();
  },
};

installTestHook(ctrl, (pattern) => ctrl.newGame({ pattern, suits: ctrl.getState().suits }));

// The not-annoying deal (assist/assist.ts): two deals of a game in a row not won -> the next one can be won.

// a tap on a window's, the menu's or a bar's control clicks it at once (Safari held clicks back during a deal)
installTapClick();

// Mahjong: the "No moves" window opens by itself (ui/mahjongHelp.ts)
watchNoMoves(ctrl);
// the card games: the "No more moves" notice, an option (ui/lostGame.ts)
watchLostGame(ctrl, () => mpMode, () => mp.done(true));

// Mahjong: a new deal takes one of the layouts that fit this board, at random (owner 2026-10-27); before the board is
// laid out, the window stands in for it. `?uklad=<name>` (fort, pyramid, cat, tree, ...) deals that layout, to show
// one. The e2e tests keep the old rule (the Turtle or the Tower, whichever has the bigger tiles) unless a test turns
// the pick on (their deals and screenshots expect the Turtle on a computer).
const mahjongBoard = (): [number, number, number] => {
  const board = document.getElementById('main');
  return [board?.clientWidth || innerWidth, board?.clientHeight || innerHeight * 0.8, bigCardsProto() ? BIG_CARD_MAX_W : CARD_MAX_W];
};
const askedLayout = MAHJONG_LAYOUTS.findIndex((l) => l.name === new URLSearchParams(location.search).get('uklad'));
const randomLayouts = !window.__E2E__ || (() => { try { return localStorage.getItem('solitaire_storage:mahjong-layouts-e2e') === 'on'; } catch { return false; } })();
/** The deal's layout was picked for this board (not a shared or stored deal's own): it may change when the screen turns. */
let layoutPicked = false;
const mahjongLayout = (game: GameId, deal: { given: number | null; fromPattern: boolean }) => {
  layoutPicked = false;
  if (deal.given !== null) return deal.given;
  // Mahjong Connect: the wide or the tall grid, whichever has the bigger tiles here (no shape to pick)
  if (game === 'mahjong-connect') {
    layoutPicked = true;
    return connectLayoutFor(...mahjongBoard());
  }
  if (askedLayout >= 0) return askedLayout;
  // the shape picked in the Games window, kept for every new deal until the player picks Mahjong itself (random)
  const picked = pickedMahjongLayout();
  if (picked !== null && !deal.fromPattern) return picked;
  // a deal stored before 2026-10-27 has no layout: the Turtle or the Tower, as it was dealt then
  if (deal.fromPattern) return mahjongLayoutFor(...mahjongBoard());
  layoutPicked = true;
  if (!randomLayouts) return mahjongLayoutFor(...mahjongBoard());
  const fits = mahjongLayoutsFor(...mahjongBoard());
  return fits[Math.floor(Math.random() * fits.length)] ?? 0;
};
/** The deal's layout still fits this board (a phone turned, a window resized). */
const layoutFits = (layout: number) => (ctrl.getState().game === 'mahjong-connect' ? connectLayoutFor(...mahjongBoard()) === layout : randomLayouts ? mahjongLayoutsFor(...mahjongBoard()).includes(layout) : mahjongLayoutFor(...mahjongBoard()) === layout);
ctrl.setLayoutChooser(mahjongLayout);
// Spider one suit: a new deal in the colour picked in the Games window (none: the pattern's own suit)
ctrl.setSuitChooser(() => spiderSuits(pickedSpiderColour()));
// A phone turned before the first move (owner 2026-10-24: the Tower stayed tiny on a phone held sideways): the deal is
// dealt again when its layout no longer fits. Once a tile is played the layout stays (the tiles have their places).
{
  let relayoutTimer = 0;
  const relayout = () => {
    window.clearTimeout(relayoutTimer);
    relayoutTimer = window.setTimeout(() => {
      const s = ctrl.getState();
      if (!RULES[s.game].tiles || mpMode || s.status !== 'idle' || s.deals === 0 || s.moves > 0 || s.history.length) return;
      if (uiStore().getState().windows.some((w) => !w.closing && !w.minimized)) return;
      if (layoutPicked && !layoutFits(s.layout)) ctrl.newGame();
    }, 400);
  };
  addEventListener('resize', relayout);
  addEventListener('orientationchange', relayout);
}

// The solo games the /admin tab counts (ui/playReport.ts): a deal's first move, and a win. A race online is the game
// server's to count.
{
  let reported = -1;
  ctrl.subscribe((s) => {
    if (!mpMode && s.moves > 0 && s.deals !== reported && s.status !== 'dealing') {
      reported = s.deals;
      reportPlay(s.game, false);
    }
  });
  ctrl.onEvent((e) => {
    if (e.type === 'won' && !mpMode) reportPlay(ctrl.getState().game, true);
  });
}

// Not in multiplayer (the server deals) nor on a /my-score/ page; in the e2e tests only when a test turns it
// on (their shuffle-parity checks compare deals with a seeded random).
const assistOn =
  env.appTarget !== 'multiplayer' &&
  pageData?.place_name !== 'my-score' &&
  (!window.__E2E__ || (() => { try { return localStorage.getItem('solitaire_storage:assist-e2e') === 'on'; } catch { return false; } })());
if (assistOn) {
  const assist: Assist = new Assist(localAssistStorage, workerSearch((g, p, proven) => assist.found(g, p, proven)));
  ctrl.setPatternProvider((g) => (mpMode ? null : assist.patternFor(g)));
  let markedDeal = -1;
  ctrl.subscribe((s) => {
    if (!mpMode && s.moves > 0 && s.deals !== markedDeal && s.status !== 'dealing') {
      markedDeal = s.deals;
      assist.played(s.game);
    }
  });
  ctrl.onEvent((e) => {
    if (e.type === 'won' && !mpMode) assist.won(ctrl.getState().game);
  });
}

// Error reports (ui/errorReport.ts): what a report says about the game and the device. The board as text (face-down
// cards as ##) and the shuffle pattern, so the owner can deal the same cards and see the same position.
const boardText = (s: EngineState) => {
  const pile = (keys: readonly number[]) => keys.map((k) => (s.faceUp[k] ? s.deck[k]!.id : '##')).join(' ');
  return {
    stock: pile(s.piles.stock), waste: pile(s.piles.waste), foundations: s.piles.foundations.map(pile),
    tableau: s.piles.tableau.map(pile), ...(s.piles.cells.length ? { cells: s.piles.cells.map(pile) } : {}),
  };
};
// The player's last steps before a report (owner 2026-10-25: "what did they do before it broke"): deals, moves,
// statuses (not the short `busy` of every move), refused moves, a drag that went back before the rules saw it
// (input/dragBack.ts), the win and the open windows; the last 30.
const steps: string[] = [];
const step = (what: string) => {
  steps.push(`${Math.round(performance.now() / 1000)}s ${what}`);
  if (steps.length > 30) steps.shift();
};
{
  let last = { game: '', deals: -1, moves: -1, status: '' };
  ctrl.subscribe((s) => {
    if (s.game !== last.game || s.deals !== last.deals) step(`deal ${s.deals} ${s.game}${s.isRetry ? ' (retry)' : ''}`);
    else if (s.moves !== last.moves) step(`moves ${s.moves}, score ${s.score}`);
    if (s.status !== last.status && s.status !== 'busy') step(`status ${s.status}`);
    last = { game: s.game, deals: s.deals, moves: s.moves, status: s.status === 'busy' ? last.status : s.status };
  });
  ctrl.onEvent((e) => {
    if (e.type === 'rejected' || e.type === 'won') step(e.type === 'won' ? 'won' : 'move refused');
  });
  onDragBack(step);
  let open = '';
  ui.subscribe(() => {
    const names = ui.getState().windows.filter((w) => !w.closing).map((w) => w.name).join(', ');
    if (names !== open) step(`windows [${(open = names)}]`);
  });
}

// ---- Kierki (hearts/, .ai/kierki.md) ---------------------------------------------------------
// The engine holds only the game id (engine/rules/hearts.ts): each of its deals of Kierki starts a Kierki game with the
// deal's shuffle pattern (New game, Retry and a report's pattern work as in the other games).
const hearts = createHeartsStore({
  rng: () => Math.random(),
  paused: () => ui.isOpen('pause') || document.visibilityState === 'hidden',
  onStep: step,
  onHandEnd: (g) => {
    ui.openWindow('hearts_score');
    saveHeartsWin(g);
  },
});
setHeartsStore(hearts);
/** A won game of Kierki goes to the player's stats and the Ranking like a solitaire win (owner 2026-10-26): won =
 *  the player's total is the lowest (a tie counts); the score is 100 minus that total; the moves are the cards the
 *  player played (13 a deal: a game has at least 4 deals, so the server's 50-move floor holds). */
function saveHeartsWin(g: HeartsState): void {
  const t = hearts.getState();
  if (g.phase !== 'over' || !heartsLeaders(g.scores).includes(0) || t.demo || env.appTarget === 'offline') return;
  const time = t.seconds, score = Math.max(0, 100 - g.scores[0]!), moves = (g.handNo + 1) * 13;
  step(`kierki won: score ${score}, ${time} s, ${moves} cards`);
  reportPlay('hearts', true);
  void post('stats/save-win.html', {
    game_type: 'hearts',
    scoring_type: 'standard',
    time,
    score,
    moves,
    prev_deal: 0,
    shuffle_pattern: g.pattern.join(','),
    hash: winHash('hearts', 'standard', time, score, moves, 0),
  });
}
// ---- Makao (makao/, .ai/makao.md) -------------------------------------------------------------
// As Kierki: the engine holds only the game id (engine/rules/makao.ts); each of its deals starts a Makao deal with the
// deal's shuffle pattern.
const makao = createMakaoStore({
  rng: () => Math.random(),
  paused: () => ui.isOpen('pause') || document.visibilityState === 'hidden',
  onStep: step,
  onEnd: (g) => {
    ui.openWindow('makao_end');
    saveMakaoWin(g);
  },
});
setMakaoStore(makao);
/** A won deal of Makao goes to the player's stats and the Ranking (owner 2026-10-27, .ai/makao.md decision 12): won =
 *  the player went out first; the score is the cards left in the computer players' hands x 10; the moves are the
 *  cards the player laid (the server's floor for Makao is lower than the solitaires' 50, server/routes/stats.js). */
function saveMakaoWin(g: MakaoState): void {
  const t = makao.getState();
  if (g.winner !== 0 || t.demo || env.appTarget === 'offline') return;
  const time = t.seconds, score = makaoWinScore(g), moves = t.laidByPlayer;
  step(`makao won: score ${score}, ${time} s, ${moves} cards`);
  reportPlay('makao', true);
  void post('stats/save-win.html', {
    game_type: 'makao',
    scoring_type: 'standard',
    time,
    score,
    moves,
    prev_deal: 0,
    shuffle_pattern: g.pattern.join(','),
    hash: winHash('makao', 'standard', time, score, moves, 0),
  });
}
// ---- Kulki (kulki/, .ai/game-rules.md "Kulki") ----------------------------------------------------
// As Kierki: the engine holds only the game id (engine/rules/kulki.ts); each of its deals starts a game of Kulki seeded
// by the deal's pattern (New game, Retry and a report's pattern work as in the other games).
const kulki = createKulkiStore({
  paused: () => ui.isOpen('pause') || document.visibilityState === 'hidden',
  onStep: step,
  onEnd: (g) => {
    // online (a race, owner 2026-11-03): a full board is "I'm done" with the final score (sent by the subscription below,
    // before this); the race's own windows show the result, and the server keeps it
    if (mpMode) return mp.done(true);
    ui.openWindow('kulki_end');
    if (g.pattern.join(',') !== raceKulki) saveKulkiGame(g);
  },
});
setKulkiStore(kulki);
// online: Kulki's score and moves go to the race as the solitaires' do from the engine (ctrl.subscribe above)
kulki.subscribe(() => {
  if (!mpMode || RULES[ctrl.getState().game].table !== 'kulki') return;
  const g = kulki.getState().game;
  if (!g || (g.score === lastStats.score && g.moves === lastStats.moves)) return;
  lastStats = { score: g.score, moves: g.moves };
  mp.updateGameStats(lastStats);
});
/** Every finished game of Kulki goes to the player's stats and the Ranking like a win (owner 2026-11-03: "każda
 *  skończona gra z wynikiem"), its points as the score; the moves are the balls moved (server/routes/stats.js has
 *  Kulki's own floor and score limit). */
function saveKulkiGame(g: KulkiState): void {
  const t = kulki.getState();
  if (t.demo || env.appTarget === 'offline') return;
  const time = t.seconds, score = g.score, moves = g.moves;
  // Retry (the end window) plays the same deal again: saved as a renewed deal, as the solitaires' wins
  const prevDeal = ctrl.getState().isRetry ? 1 : 0;
  step(`kulki saved: score ${score}, ${time} s, ${moves} moves${prevDeal ? ', retry' : ''}`);
  reportPlay('kulki', true);
  void post('stats/save-win.html', {
    game_type: 'kulki',
    scoring_type: 'standard',
    time,
    score,
    moves,
    prev_deal: prevDeal,
    shuffle_pattern: [...g.pattern, g.line].join(','), // the version after the 52 numbers (engine/gameRegistry.ts)
    hash: winHash('kulki', 'standard', time, score, moves, prevDeal),
  });
}
// ---- 3-5-8 (358/, .ai/358.md) ------------------------------------------------------------------
// As Kierki: the engine holds only the game id (engine/rules/threeFiveEight.ts); each of its deals starts a 3-5-8 game
// of the length picked in the Games window, with the deal's shuffle pattern.
const t58 = create358Store({
  rng: () => Math.random(),
  paused: () => ui.isOpen('pause') || document.visibilityState === 'hidden',
  onStep: step,
  onHandEnd: (g) => {
    ui.openWindow('t58_score');
    save358Win(g);
  },
});
set358Store(t58);
/** A won game of 3-5-8 goes to the player's stats and the Ranking (.ai/358.md): won = the player's total is the
 *  highest after the last deal (a tie counts); the score is that total x 10; the moves are the cards the player played
 *  (16 a deal; the server's floor for 3-5-8 is lower than the solitaires' 50, server/routes/stats.js). */
function save358Win(g: State358): void {
  const st = t58.getState();
  if (!won358(g) || st.demo || env.appTarget === 'offline') return;
  const time = st.seconds, score = winScore358(g), moves = st.playedByPlayer;
  step(`3-5-8 won: score ${score}, ${time} s, ${moves} cards`);
  reportPlay('three-five-eight', true);
  void post('stats/save-win.html', {
    game_type: 'three-five-eight',
    scoring_type: 'standard',
    time,
    score,
    moves,
    prev_deal: 0,
    // the length after the 52 cards: the Ranking filters by it (server/routes/stats.js VARIANTS)
    shuffle_pattern: [...g.pattern, g.length].join(','),
    hash: winHash('three-five-eight', 'standard', time, score, moves, 0),
  });
}
if (window.__E2E__) {
  (window as unknown as { __358Test: unknown }).__358Test = {
    /** Shows any position (tests, the owner's screenshots); never saved as a win. */
    load(g: State358) {
      t58.load(g);
    },
    backdate(ms: number) {
      t58.backdate(ms);
    },
    /** The running game's length in deals (the Games window's choice, tests/358). */
    length() {
      return t58.getState().game?.length ?? null;
    },
  };
}
// ---- Tysiąc (1000/, .ai/tysiac.md) -------------------------------------------------------------
// As 3-5-8: the engine holds only the game id (engine/rules/thousand.ts); each of its deals starts a game to the points
// picked in the Games window, with the deal's shuffle pattern (the 52 shuffled, the 24 from 9 to ace kept).
const t1k = create1000Store({
  rng: () => Math.random(),
  paused: () => ui.isOpen('pause') || document.visibilityState === 'hidden',
  onStep: step,
  onHandEnd: (g) => {
    ui.openWindow('t1k_score');
    save1000Win(g);
  },
});
set1000Store(t1k);
/** A won game of Tysiąc goes to the player's stats and the Ranking (.ai/tysiac.md): won = the player reached the target
 *  (only a declarer can); the score is the player's total; the moves are the cards the player played (8 a deal; a game
 *  to 500 takes 2 deals at least: the server's floor for it is 16, server/routes/stats.js). */
function save1000Win(g: State1000): void {
  const st = t1k.getState();
  if (!won1000(g) || st.demo || env.appTarget === 'offline') return;
  const time = st.seconds, score = winScore1000(g), moves = st.playedByPlayer;
  step(`1000 won: score ${score}, ${time} s, ${moves} cards`);
  reportPlay('thousand', true);
  void post('stats/save-win.html', {
    game_type: 'thousand',
    scoring_type: 'standard',
    time,
    score,
    moves,
    prev_deal: 0,
    // the target after the 52 cards: the Ranking filters by it (server/routes/stats.js VARIANTS)
    shuffle_pattern: [...g.pattern, g.target].join(','),
    hash: winHash('thousand', 'standard', time, score, moves, 0),
  });
}
if (window.__E2E__) {
  (window as unknown as { __1000Test: unknown }).__1000Test = {
    /** Shows any position (tests, the owner's screenshots); never saved as a win. */
    load(g: State1000) {
      t1k.load(g);
    },
    backdate(ms: number) {
      t1k.backdate(ms);
    },
    /** The running game's target (the Games window's choice). */
    target() {
      return t1k.getState().game?.target ?? null;
    },
  };
}
if (window.__E2E__) {
  (window as unknown as { __makaoTest: unknown }).__makaoTest = {
    /** Shows any position (tests, the owner's screenshots); never saved as a win. */
    load(g: MakaoState) {
      makao.load(g);
    },
  };
}
if (window.__E2E__) {
  (window as unknown as { __kulkiTest: unknown }).__kulkiTest = {
    /** Shows any position (tests, the owner's screenshots); a full board opens the end window; never saved. */
    load(g: KulkiState) {
      kulki.load(g);
    },
    state: () => kulki.getState(),
  };
}
// e2e only: the windows with an ad, opened without playing (e2e/tools/ad-windows-sweep.mjs)
if (window.__E2E__) {
  (window as unknown as { __windowsTest: unknown }).__windowsTest = {
    win() {
      const s = ctrl.getState();
      const demo: WinData = { game: s.game, scoring: s.scoring, time: 187, score: 455, moves: 96, prevDeal: false, pattern: s.pattern.slice(), demo: true };
      ui.openWindow('win', demo as unknown as Record<string, unknown>);
    },
    noMoves() {
      ui.openWindow('no_moves', { cards: true });
    },
  };
}
// e2e only: the score window after a long game, without playing it (tests/hearts, the owner's screenshots)
if (window.__E2E__) {
  (window as unknown as { __heartsTest: unknown }).__heartsTest = {
    /** Shows any position (the rules pictures: e2e/tools/rules-shots/hearts.spec.ts); never saved as a win. */
    load(g: HeartsState) {
      hearts.load(g);
    },
    /** The game began `ms` earlier (a long game's New game reloads, hearts/nextGame.ts). */
    backdate(ms: number) {
      hearts.backdate(ms);
    },
    endDeals(history: number[][], over = false) {
      const g = hearts.getState().game;
      if (!g) return;
      const scores = [0, 1, 2, 3].map((p) => history.reduce((s, h) => s + (h[p] ?? 0), 0));
      hearts.load({ ...g, handNo: history.length - 1, history, scores, trick: [], turn: -1, winner: -1, moon: -1, phase: over ? 'over' : 'hand-end', hands: [[], [], [], []] });
    },
  };
}
// the engine's own deal of a Kierki game has no card to show: it ends at once (it took ~2 s of gathering and spreading
// nothing before the Kierki deal began, owner 2026-10-26)
ctrl.subscribe((s) => {
  if (s.status === 'dealing' && RULES[s.game].table) queueMicrotask(() => ctrl.finishDeal());
});
// The table games' first card for the /admin tab (ui/playReport.ts): they have no moves in `ctrl`, each store tells.
{
  const watchTable = <S extends { startedAt: number | null; demo: boolean }>(game: string, store: { getState(): S; subscribe(fn: () => void): () => void }, moved: (s: S) => boolean) => {
    let marked: number | null = null;
    store.subscribe(() => {
      const s = store.getState();
      if (mpMode || mpCards !== null || s.demo || s.startedAt === marked || !moved(s)) return;
      marked = s.startedAt;
      reportPlay(game, false);
    });
  };
  watchTable('hearts', hearts, (s) => !!s.game && (s.game.phase !== 'pass' || s.game.handNo > 0));
  watchTable('makao', makao, (s) => s.laidByPlayer > 0);
  watchTable('three-five-eight', t58, (s) => s.playedByPlayer > 0);
  watchTable('thousand', t1k, (s) => s.playedByPlayer > 0);
  watchTable('kulki', kulki, (s) => (s.game?.moves ?? 0) > 0);
}

ctrl.onEvent((e) => {
  if (e.type !== 'dealt') return;
  const s = ctrl.getState();
  const table = RULES[s.game].table;
  for (const w of ['hearts_score', 'makao_end', 't58_score', 't1k_score', 'kulki_end'] as const) if (ui.isOpen(w)) ui.closeWindow(w);
  // an online card game is dealt by the server (mpCards, any card game): the offline table stays still
  const local = (t: typeof table) => table === t && !(mpCards !== null && toGameId(mpCards) === s.game);
  if (local('hearts')) hearts.newGame(s.pattern);
  else hearts.stop();
  if (local('makao')) makao.newGame(s.pattern);
  else makao.stop();
  if (local('358')) t58.newGame(s.pattern, picked358Length());
  else t58.stop();
  if (local('1000')) t1k.newGame(s.pattern, picked1000Target());
  else t1k.stop();
  // the version picked in the Games window; a race online is always 5 in a row (the catalog has that one)
  if (table === 'kulki') kulki.newGame(s.pattern, mpMode ? 5 : pickedKulkiLine());
  else kulki.stop();
});
const anchorHeight = () => {
  try {
    return Number(localStorage.getItem('solitaire_storage:ads:anchor-h')) || 0;
  } catch {
    return null;
  }
};
setReportContext(() => {
  const s = ctrl.getState();
  const u = ui.getState();
  const vv = window.visualViewport;
  const nav = navigator as Navigator & { deviceMemory?: number; connection?: { effectiveType?: string; saveData?: boolean } };
  return {
    game: s.game, status: s.status, deals: s.deals, moves: s.moves, score: s.score, seconds: s.seconds, won: s.won,
    retry: s.isRetry, recycles: s.recycles, undoSteps: s.history.length, pattern: s.pattern.join(','), board: RULES[s.game].table === 'makao' ? makaoBoardText(makao.getState()) : RULES[s.game].table === 'kulki' ? kulkiBoardText(kulki.getState()) : RULES[s.game].table === '358' ? boardText358(t58.getState()) : RULES[s.game].table === '1000' ? boardText1000(t1k.getState()) : RULES[s.game].table ? heartsBoardText(hearts.getState()) : boardText(s),
    ...(RULES[s.game].tiles ? { mahjongLayout: layoutOf(s).name, hints: s.hints ?? 0 } : {}),
    windows: u.windows.filter((w) => !w.closing).map((w) => w.name), options: { option: u.options.option, skin: u.options.skin },
    language: currentLanguage(), target: env.appTarget, multiplayer: mpMode, translated: pageTranslated(), pageLang: document.documentElement.lang,
    screen: `${innerWidth}x${innerHeight}, ekran ${screen.width}x${screen.height} @${Math.round(devicePixelRatio * 100) / 100}x${vv ? `, widoczne ${Math.round(vv.width)}x${Math.round(vv.height)} zoom ${Math.round(vv.scale * 100) / 100}` : ''}`,
    touch: navigator.maxTouchPoints, orientation: matchMedia('(orientation: portrait)').matches ? 'pionowo' : 'poziomo',
    online: navigator.onLine, secondsSinceLoad: Math.round(performance.now() / 1000), visible: document.visibilityState,
    // what the old contact text said about the device, and what helps to find a fault
    device: {
      orientationType: (screen.orientation as ScreenOrientation | undefined)?.type ?? null,
      pointerCoarse: matchMedia('(pointer: coarse)').matches, mobileLayout: isMobile,
      systemReducedMotion: matchMedia('(prefers-reduced-motion: reduce)').matches,
      installedApp: matchMedia('(display-mode: standalone)').matches,
      memoryGb: nav.deviceMemory ?? null, cpuCores: navigator.hardwareConcurrency ?? null,
      connection: nav.connection?.effectiveType ?? null, saveData: nav.connection?.saveData ?? null,
      browserLanguage: navigator.language, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    effects: { option: u.options.option.effects, on: !effectsOff() },
    adAnchorHeight: anchorHeight(), loggedIn: !!u.player, referrer: document.referrer || null,
    steps: steps.slice(),
  };
});
// A board that stays in a deal or an animation (status dealing / busy with no change) for HANG_MS of visible time:
// reported once. A hidden tab stops the animations, so its time does not count. A deal is then ended where it is
// (three reports of 2026-10-28/29: the dealt cards shown, "dealing" for 25-115 s, nothing could be moved); the report
// says whether the deal still had its timer and next phase, and the page's earlier errors (ui/errorReport.ts).
// Time in which the page itself did not run does not count either (2026-10-29/30: "dealing" for 299 and 422 s with the
// deal's timer still set; this tick had not run for minutes, so the device slept or the browser froze the tab, and on
// waking this tick ran before the deal's own timer; a visibility change is not always seen first).
{
  const HANG_MS = 20_000;
  const TICK_MS = 2000;
  /** A tick this much later than the one before: the page was asleep, not the deal. */
  const ASLEEP_MS = 10_000;
  let since = 0;
  let seq = -1;
  let reported = false;
  let lastTick = performance.now();
  setInterval(() => {
    const s = ctrl.getState();
    const now = performance.now();
    const slept = now - lastTick > TICK_MS + ASLEEP_MS;
    lastTick = now;
    if (reported || slept || document.visibilityState !== 'visible' || (s.status !== 'dealing' && s.status !== 'busy') || s.seq !== seq) {
      seq = s.seq;
      since = now;
      return;
    }
    if (performance.now() - since >= HANG_MS) {
      reported = true;
      const dealing = s.status === 'dealing';
      reportError('hang', `the board stayed "${s.status}" for ${Math.round((performance.now() - since) / 1000)} s`, undefined, dealing ? { dealPending: ctrl.dealPending(), rescued: true } : undefined);
      if (dealing) ctrl.finishDeal();
    }
  }, 2000);
}

injectIcons(); // the --i-<name> mask variables the shell and window glyphs are painted with (ui/icons.ts)
installTapToast(); // touch: a short word over a footer tile after a tap (ui/tapToast.ts)
installBackGuard(); // touch: Back (Android's edge gesture) does not leave the game at once (ui/backGuard.ts)
installPillRedraw(); // the top bar's words drawn again where they are after the pills slide (ui/pillRedraw.ts)
installDomGuard(); // a page translated by the browser no longer blanks the app (ui/domGuard.ts)
const root = document.getElementById('root');
if (!root) throw new Error('#root is missing: the page template must render <div id="root"></div>');
// a crash of the view leaves an empty board: reported (React still writes it to the console)
(window.__bootStages ??= {}).render = Math.round(performance.now()); // the start watchdog's steps (main.tsx)
createRoot(root, {
  onUncaughtError: (err, info) => {
    console.error(err);
    reportError('react', err instanceof Error ? err.message : String(err), `${err instanceof Error ? err.stack ?? '' : ''}\n\nComponents:${info.componentStack ?? ''}`);
  },
  // A render that threw once and worked when React did it again (React's error #520, 2026-10-25 report from Firefox):
  // the board went on, but React's own report named only its wrapper. The error that was thrown is in `cause`.
  onRecoverableError: (err, info) => {
    const cause = err instanceof Error && (err as { cause?: unknown }).cause !== undefined ? (err as { cause?: unknown }).cause : err;
    console.error(cause);
    reportError('js', `React recovered from: ${cause instanceof Error ? cause.message : String(cause)}`, `${cause instanceof Error ? cause.stack ?? '' : ''}\n\nComponents:${info.componentStack ?? ''}`);
  },
}).render(
  <StrictMode>
    <GameContext value={ctrl}>
      <App actions={actions} />
    </GameContext>
  </StrictMode>,
);

// Like legacy: preload the 52 faces of the deck, the back, the background and the empty slot, then deal. The ones not
// come yet are in window.__bootPending: the start watchdog's report names them (2026-10-25: one of 55 never came on a
// slow-2g line, and the game waited the whole 15 s for it). The first deal is decided ahead (ctrl.planDeal), its
// face-up cards' faces are asked first, and the deal waits (up to 15 s, as before) for those, the back, the background
// and the slot only; the other faces come meanwhile (owner 2026-10-29 after the slow start reports of that day: on a 3g
// line the game waited 15-23 s for 52 faces when the first Klondike deal shows 7 of them). A face that comes after its
// card shows a plain card until then (board/CardEl.tsx data-wait). The other faces are asked for only once those are
// in (2026-10-30, measured on a slow line): asked together, they all share the line and the face-up ones came last with
// them, the deal waited 6.9 s, and the consent window and the ads waited behind 700 KB of pictures too.
// The plan waits one timer turn: under the tests main.tsx keeps Math.random constant until this module has run (React's module-init
// calls stay off the seeded stream), and a plan made now dealt every player the same unshuffled cards; after it the
// plan draws exactly what the deal itself drew before (the e2e seeds deal the same cards).
const deckType = options.skin.deckType;
let plan: ReturnType<typeof ctrl.planDeal> = null;
/** The card pictures a start without cards (Kulki, Mahjong) leaves for later. */
let cardsLater: PreloadImage[] = [];
window.__bootPending = new Set();
const preloaded = new Promise<void>((r) => setTimeout(r, 0)).then(() => {
  plan = env.appTarget !== 'multiplayer' && pageData?.place_name !== 'my-score' ? ctrl.planDeal() : null;
  // Kulki and the Mahjong games show no card: their first board waits for the background (and Kulki's 7 balls, Mahjong's
  // empty slot) only, and the deck's pictures come later for a card game picked afterwards: Mahjong's after its tiles,
  // Kulki's 3 s after the start (owner 2026-11-03, the asset check: at 1.6 Mb/s the 578 KB of faces held Kulki's board
  // back to 10.2 s, the last face at 9.5 s; Mahjong's tiles were asked only after them)
  const start = RULES[ctrl.getState().game];
  if (env.appTarget !== 'multiplayer' && (start.table === 'kulki' || start.tiles)) {
    const set = ballSetOf(options.skin.deckType, options.skin.ballType);
    const own = start.tiles ? [emptySlotSrc(options.skin.backgroundType)] : Array.from({ length: COLOURS }, (_, i) => ballSrc(set, i + 1));
    cardsLater = [...faceImages(deckType, CARD_IDS), backImage(options.skin.cardBackType), emptySlotSrc(options.skin.backgroundType)];
    return preloadImages([backgroundSrc(options.skin.backgroundType, smallBackground()), ...own], undefined, window.__bootPending);
  }
  const firstUp = new Set(plan?.faceUp ?? CARD_IDS);
  const upIn = preloadImages(faceImages(deckType, CARD_IDS.filter((id) => firstUp.has(id))), undefined, window.__bootPending);
  const basicsIn = preloadImages([backImage(options.skin.cardBackType), backgroundSrc(options.skin.backgroundType, smallBackground()), emptySlotSrc(options.skin.backgroundType)], undefined, window.__bootPending);
  return Promise.all([upIn, basicsIn]).then(([up, basics]) => {
    void preloadImages(faceImages(deckType, CARD_IDS.filter((id) => !firstUp.has(id))), undefined, window.__bootPending);
    return up && basics;
  });
});
void preloaded.then((all) => {
  // for the start watchdog's report (main.tsx's steps): when the pictures came, or that the wait for them ran out
  const later = window.__bootPending?.size ?? 0;
  const stage = !all ? 'pictures (not all after 15 s)' : later ? `pictures (dealt, ${later} faces still coming)` : 'pictures';
  (window.__bootStages ??= {})[stage] = Math.round(performance.now());
  // --vvh (the visual viewport height for the phone sheets, ui/vvh.ts) only now: reading
  // visualViewport.height forces a layout, which fetches the body background before the 52 faces
  // (BOOT-006 pins the legacy order); until then the sheets use their 100dvh fallback.
  installVvh();
  startupDone(); // the start-up preload was the indicator's first action (ui/busy.ts)
  // The sounds only after the first deal (2026-11-01, Web Vitals: built at the start, they kept the table's picture from
  // the screen on a desktop); the longest deal, Spider's, takes 54 x 45 + 360 ms
  setTimeout(startSoundBuilding, 3000);
  window.__solitaireStarted = true; // the page template's start watchdog (server/views/_base.html) stands down
  // Mahjong: its tile pictures, once per tile set (the deck option's, assets.ts tileDeckOf), the first time the game
  // is Mahjong and again when the deck changes while it is (both sets: the flat ones show on the deck). Only after the
  // start-up preload: loaded with it, 172 pictures held the top bar and the windows back for seconds on a phone's Wi-Fi
  // (owner 2026-10-24: "klikam graj i ciągle nic")
  {
    const tilesLoaded = new Set<string>();
    const loadTiles = (game: GameId, skin: { deckType: string; tileType?: string | undefined }) => {
      if (!RULES[game].tiles) return;
      const deck = tileDeckOf(skin.deckType, skin.tileType);
      if (tilesLoaded.has(deck)) return;
      tilesLoaded.add(deck);
      return preloadImages(tileImages(deck, [...MAHJONG_TILES, ...MAHJONG_BONUS]));
    };
    const tilesIn = loadTiles(ctrl.getState().game, options.skin);
    if (cardsLater.length) void (tilesIn ?? new Promise((r) => setTimeout(r, 3000))).then(() => preloadImages(cardsLater));
    ctrl.subscribe((s) => loadTiles(s.game, uiStore().getState().options.skin));
    uiStore().subscribe(() => loadTiles(ctrl.getState().game, uiStore().getState().options.skin));
  }
  void silentLogin();
  const preview = pageData?.place_name === 'my-score';
  // A /my-score/ page deals the shared win's cards, so the visitor can play the same deal.
  let sharedDeal = false;
  if (env.appTarget !== 'multiplayer' && ctrl.getState().deals === 0 && ctrl.getState().status !== 'dealing') {
    const shared = preview ? sharedPattern(pageData?.place_pattern) : null;
    if (shared) {
      try {
        sharedDeal = ctrl.newGame({ pattern: shared });
      } catch {
        sharedDeal = false; // a pattern of another deck size: a normal deal instead
      }
    }
    // the planned deal (its face-up pictures are in), unless the game changed meanwhile
    if (!sharedDeal) ctrl.newGame(plan && plan.game === ctrl.getState().game ? { pattern: plan.pattern } : undefined);
  }
  // a friend's link (multiplayer.html?t=K7QX), the lobby again after its "Lobby" reload, the multiplayer page
  const friendCode = /^[A-Za-z0-9]{4}$/.test(new URLSearchParams(location.search).get('t') ?? '') ? new URLSearchParams(location.search).get('t')! : null;
  let mpOpened = false;
  if (friendCode) {
    mp.open({ code: friendCode });
    mpOpened = true;
  } else if (mp.reopenAfterReload()) mpOpened = true;
  else if (env.appTarget === 'multiplayer') setTimeout(() => mp.open(), 1000);
  else if (mp.resumeWaiting()) mpOpened = true; // straight to the new deal under the bar, no Games window over it
  setTimeout(() => ui.set({ startupDone: true }), 1500);
  // The Win window's saloon type (fonts/saloon.css): fetched early, so "Congratulations!" never shows the fallback first.
  setTimeout(() => void document.fonts?.load('400 32px Saloon').catch(() => undefined), 2000);
  if (!isMobile && !preview && !mpOpened && env.appTarget !== 'multiplayer' && env.appTarget !== 'embedded-rtp') {
    setTimeout(() => uiStore().openWindow('games'), 250);
    // (no "What's new" window any more: removed 2026-10-17, owner: "stara zaszłość")
  }
  if (preview && pageData) {
    const p = pageData.place_params;
    const data: WinData = {
      game,
      scoring: p.score_type === 'timed' ? 'timed' : 'standard',
      time: Number(p.time) || 0,
      score: Number(p.score) || 0,
      moves: Number(p.moves) || 0,
      prevDeal: false,
      pattern: [],
      preview: true,
      winnerName: pageData.place_winner_name,
    };
    setTimeout(() => ui.openWindow('win', data as unknown as Record<string, unknown>), 1000);
  }
});
